import { ArrowUpward } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setTemplateSelectedItem, setTemplateSortSelectedItem } from 'app/actions/checklist';
import { Autosizer, Scrollbar } from 'app/components';
import React, { createContext, forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const ChecklistTemplateListContext = createContext();

const ChecklistTemplateList = () => {
  const dispatch = useDispatch();
  const { templateItems, templateSelectedItem, templateSearchValue, templateSortSelectedItem } = useSelector((state) => state.checklist);

  const sortItems = useMemo(() => {
    const compareFn = (a, b) => {
      switch (templateSortSelectedItem.name) {
        case 'created_at':
        case 'updated_at':
          switch (templateSortSelectedItem.order) {
            case 'ASC':
              return new Date(a[templateSortSelectedItem.name]) - new Date(b[templateSortSelectedItem.name]);
            case 'DESC':
              return new Date(b[templateSortSelectedItem.name]) - new Date(a[templateSortSelectedItem.name]);
          }
          break;
        case 'name':
          switch (templateSortSelectedItem.order) {
            case 'ASC':
              return a[templateSortSelectedItem.name].localeCompare(b[templateSortSelectedItem.name], {
                sensitivity: 'base',
              });
            case 'DESC':
              return b[templateSortSelectedItem.name].localeCompare(a[templateSortSelectedItem.name], {
                sensitivity: 'base',
              });
          }
          break;
        case 'active':
          switch (templateSortSelectedItem.order) {
            case 'ASC':
              return a.active - b.active;
            case 'DESC':
              return b.active - a.active;
            default:
              break;
          }
          break;
      }
    };

    if (templateSearchValue.length) {
      return templateItems.filter((item) => item.name.toLowerCase().indexOf(templateSearchValue.toLowerCase()) !== -1).sort(compareFn);
    }

    return templateItems.sort(compareFn);
  }, [templateItems, templateSelectedItem, templateSearchValue, templateSortSelectedItem]);

  const handleItemClick = (value) => () => dispatch(setTemplateSelectedItem(value));

  const handleSortClick = (value) => () => dispatch(setTemplateSortSelectedItem(value));

  return (
    <Box
      component='div'
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-horizontal': {
          right: '10px',
        },
        '.simplebar-vertical': {
          top: '30px',
          bottom: '0',
        },
        '.MuiListItem-root': {
          width: '100%',
          userSelect: 'none',
          padding: theme.spacing(0),
          borderRadius: '0',
        },
      })}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <ChecklistTemplateListContext.Provider
              value={{
                sortItemClick: handleSortClick,
                sortSelectedItem: templateSortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    itemSize={24}
                    overscanCount={10}
                    itemCount={sortItems.length}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    innerElementType={TemplateListInnerElementType}
                    itemData={{
                      items: sortItems,
                      selectedItem: templateSelectedItem,
                      onClick: handleItemClick,
                    }}>
                    {TemplateListItemRender}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </ChecklistTemplateListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const TemplateListInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <ChecklistTemplateListContext.Consumer>
    {({ sortItemClick, sortSelectedItem }) => (
      <List
        ref={ref}
        disablePadding
        sx={{
          height: style.height,
          minHeight: '100%',
        }}
        {...rest}>
        <ListItem
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: style.height,
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          {config.checklistTemplateColumns.map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            position: 'sticky !important',
            top: '0',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:first-of-type': {
                '.MuiListItemText-root': {
                  paddingLeft: theme.spacing(2),
                },
              },
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          {config.checklistTemplateColumns.map((column) => (
            <ListItemButton
              key={column.name}
              onClick={sortItemClick(column)}
              disableTouchRipple={!column.order}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                <Typography>{column.label}</Typography>
                <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </List>
    )}
  </ChecklistTemplateListContext.Consumer>
));

const TemplateListItemRender = ({ data, index, style }) => {
  const { items, selectedItem, onClick } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      selected={items[index].id === selectedItem?.id}
      onClick={onClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      {config.checklistTemplateColumns.map((column) => (
        <ListItemText key={column.name} sx={{ width: column.width }}>
          {<Typography noWrap>{column.format ? column.format(items[index][column.name]) : items[index][column.name]}</Typography>}
        </ListItemText>
      ))}
    </ListItem>
  );
};

export default ChecklistTemplateList;
